import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { showNotification } from 'src/app/helpers/show-toast';
import { PatientSubscribeToPSP } from 'src/app/_models/PSP/psp-new-patient-sub.model';
import { PSPPatient } from 'src/app/_models/PSP/psp-patient.model';
import { PSPProgram } from 'src/app/_models/PSP/psp-program-model';
import { AreaService } from 'src/app/_services/area.service';
import { CityService } from 'src/app/_services/city.service';
import { LookupModalService } from 'src/app/_services/PSP/modals.service';
import { PspProgramsService } from 'src/app/_services/PSP/psp-programs.service';
import { CompanyService } from 'src/app/_services/company.service';
import { PspTasksService } from 'src/app/_services/PSP/psp-tasks.service';
import { IsPermissionExist } from 'src/app/helpers/get-permissions';
import { PspEvaPermission, PspLillyPermission, PspRochePermission } from 'src/app/_constants/permissions.constant';
import { RocheDoctor } from '../../../_models/PSP/psp-RocheDoctor.model';
import { PspCompanyEnum } from '../../../enums/psp-company.enum';
import { DoctorsService } from '../../../_services/doctors.service';
import { InsulinCenter, InsulinType } from 'src/app/_models/PSP/psp-insulin.model';
import { PspInsulinService } from 'src/app/_services/PSP/psp-insulin.service';
import { ProgramNameEnum } from 'src/app/enums/psp-insulin-project-enums';

@Component({
  selector: 'app-adding-caregiver',
  templateUrl: './adding-caregiver.component.html',
  styleUrls: ['./adding-caregiver.component.scss'],
})
export class AddingCaregiverComponent implements OnInit {
  relations = [];
  cities = [];
  areas = [];
  companies = [];
  programList: PSPProgram[];
  user = {} as PSPPatient;
  newPatient = {} as PatientSubscribeToPSP;
  companyId?: number = null;
  specialtyId?: number = null;
  isPreFetched = false;
  rocheDoctors: RocheDoctor[] = [];
  selectedRocheDoctor?: string;
  insulinTypes?: InsulinType[];
  referralCenters?: InsulinCenter[];
  uploadFileTypes: string = ".jpg, .jpeg, .png, .gif, .pdf";
  uploadedDocuments: File[] = [];
  maxDocumentsToUpload = 10;
  // TODO: extract into an api
  rocheTypes = [
    { id: 'A', name: 'HCP' },
    { id: 'C', name: 'Private Hospital' },
    { id: 'D', name: 'Cleopatra Group' },
    { id: 'N', name: 'NGO' }
  ];
  selectedRocheType: string;
  selectedRocheSerialNumber: string;
  serialNumbersByType: any[];
  careReceiverDOB = '';
  displayErrorInSubscriptionHistory = false;
  readonly Number = Number;

  constructor(
    private pspTaskService: PspTasksService,
    private router: Router,
    private companyService: CompanyService,
    private areaService: AreaService,
    private cityService: CityService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private programService: PspProgramsService,
    private myModalService: LookupModalService,
    private DoctorService: DoctorsService,
    private pspInsulinService: PspInsulinService
  ) {}

  ngOnInit(): void {
    this.spinner.show();

    this.companyService.getAllCompanies().subscribe((response) => {
      this.companies = response.data?.companies.filter(c => this.hasCompanyPermission(c.id));
    });

    this.cityService.getAllCities().subscribe((response) => {
      this.cities = [...response];
    });

    this.pspTaskService.getPspRelations().subscribe((response) => {
      this.relations = response?.data.relations;
    });
  }

  onCopy(): void {
    const copyText = this.newPatient.patientMobileNumber;

    navigator.clipboard.writeText(copyText);
  }

  receiveUser(event: PSPPatient): void {
    this.isPreFetched = true;
    this.user = event;
    this.newPatient.patientFullName = event.fullName;
    this.newPatient.patientMobileNumber = event.phone;
    this.newPatient.patientDOB = new Date(event.birthdate);
    this.newPatient.patientCityId = event.cityId;
    this.newPatient.patientNationalId = event.nationalId;
    this.newPatient.patientAreaId = event.areaId;

    this.loadAreas();
  }

  onNationalIdChange(isPassportActive: boolean): void {
    if (!isPassportActive) {
      this.newPatient.careReceiverPassportId = null;
    } else {
      this.newPatient.careReceiverNationalId = null;
    }
  }

  onCityChange(event: number): void {
    this.spinner.show();

    this.newPatient.patientCityId = event;
    this.newPatient.patientAreaId = null;
    this.loadAreas();
  }

  private loadAreas(cityId?: number): void {
    if (!cityId && !this.newPatient.patientCityId) {
      return;
    }

    this.areaService
      .getAreasByCityId(cityId ?? this.newPatient.patientCityId)
      .subscribe((res) => {
        this.areas = [...res.data.areas];

        this.spinner.hide();
      });
  }

  onRocheTypeChange(event: any): void {
    this.serialNumbersByType = [];
    this.selectedRocheDoctor = null;
    this.selectedRocheSerialNumber = null;

    if (this.rocheDoctors == null || this.selectedRocheType == null) {
      return;
    }

    for (const item of this.rocheDoctors) {
      if (item.code.find(e => e.toLowerCase() === this.selectedRocheType.toLowerCase())) {
        this.serialNumbersByType.push(item.serialNumber);
      }
    }

    this.serialNumbersByType.sort((a: string, b: string) => {
      return +a - +b; // Convert strings to numbers for numerical sorting
    });
  }

  onDoctorSerialNumberChange(event: Event): void {
    this.selectedRocheDoctor = null;

    if (this.selectedRocheType == null || this.selectedRocheSerialNumber == null) {
      return;
    }

    const doctor = this.rocheDoctors.find(item =>
        item.serialNumber === this.selectedRocheSerialNumber && item.code.find(c => c === this.selectedRocheType)
    );

    this.selectedRocheDoctor = this.formatDoctorName(doctor);
  }

  onCompanyChange(event: any): void {
    this.spinner.show();

    this.newPatient.programId = null;
    this.newPatient.subscriptionHistory = null;
    this.serialNumbersByType = [];
    this.selectedRocheType = null;
    this.selectedRocheDoctor = null;
    this.selectedRocheSerialNumber = null;

    this.companyId = Number.parseInt(event, 10);
    this.programList = [];

    if (isNaN(this.companyId))
    {
      this.companyId = null;
      return;
    }

    this.programService
      .getAllProgramsByCompanyId(this.companyId)
      .subscribe((res) => {
        this.programList = [...res.data.programs];
      });

    if (this.companyId === PspCompanyEnum.Roche) {
      this.newPatient.hospitalName = null;
      this.newPatient.doctorName = null;

      this.DoctorService
          .getDoctorsByCompanyId(this.companyId)
          .subscribe((res) => {
            this.rocheDoctors = res.data.doctors;
            this.spinner.hide();
          });
    }

    if(this.companyId === PspCompanyEnum.Eva){
      this.pspInsulinService.getInsulinTypes().subscribe((res) => {
        this.insulinTypes = res.data;
      });
    }

  }

  showRocheFields(): boolean {
    return this.companyId === PspCompanyEnum.Roche;
  }

  preventWhiteSpace(event: KeyboardEvent): void {
    const inputValue = (event.target as HTMLInputElement).value;
    if(inputValue == ' ')
    {
      this.newPatient.subscriptionHistory ='';
    }
  }
  PreventWhiteSpaceAndNotifyWhenExceedMaxChars(event: KeyboardEvent): void {
    const cursorIndex: number = (event.target as HTMLInputElement).selectionStart;
    const subscriptionHistoryValue: string = this.newPatient.subscriptionHistory;
    this.displayErrorInSubscriptionHistory = false;
    const isAlphabetic = /^[a-zA-Z0-9\u0600-\u06FF\u0660-\u0669\u0670-\u06D3\u06D5\u06E5\u06E6!@#$%^&*()-_=+[\]{}|;:'",.<>/?\\`~\s]$/.test(event.key);
    const shortCuts= event.ctrlKey && (['a','c','x','z','v'].includes(event.key));

    if (event.key == ' ' && (subscriptionHistoryValue == '' || subscriptionHistoryValue == null
      || cursorIndex == 0)) {
      event.preventDefault();
    }

    else if (subscriptionHistoryValue != null && subscriptionHistoryValue.length >= 50
      && isAlphabetic && !shortCuts) {
      event.preventDefault();
      this.displayErrorInSubscriptionHistory = true;
    }

  }
  onAddNewCareReceiver(form: NgForm): void {
    this.newPatient.pspTypeId = 2;
    this.newPatient.careReceiverDOB = form.value.dob ? new Date(form.value.dob) : this.user.birthdate;
    this.newPatient.doctorCode = null;
    if (this.companyId && this.companyId === PspCompanyEnum.Roche) {
      this.newPatient.doctorCode = `${this.selectedRocheType}-${this.selectedRocheSerialNumber}`;
      this.newPatient.doctorName = this.selectedRocheDoctor;
    }

    let responseData= this.changeResponseToFormData();

    this.spinner.show();
    this.pspTaskService.addNewPatient(responseData).subscribe((res) => {
      showNotification(
        'success',
        `${this.newPatient.patientFullName} has been Added`,
        this.toastr
      );
      this.spinner.hide();
      this.router.navigateByUrl('psp/daily-tasks');
    });
  }

  onReset(form: NgForm): void {
    form.reset();
    this.user = {} as PSPPatient;
    this.newPatient = {} as PatientSubscribeToPSP;
    this.router.navigateByUrl('psp/daily-tasks');
  }

  openFetchModal(): void {
    this.myModalService.openLookupModal();
  }

  private hasCompanyPermission(companyId: number): boolean {
    switch (companyId) {
      case PspCompanyEnum.Eva:
        return IsPermissionExist(PspEvaPermission);
      case PspCompanyEnum.Lilly:
        return IsPermissionExist(PspLillyPermission);
      case PspCompanyEnum.Roche:
        return IsPermissionExist(PspRochePermission);
      default:
        return false;
    }
  }

  private formatDoctorName({englishName, arabicName}): string {
    return [englishName, arabicName]
      .filter(name => name !== null && name !== undefined && name !== '')
      .join(' - ');
  }

  onProgramChange(): void {
    this.newPatient.referralCenterId = null;
    this.newPatient.insulinTypeId = null;

    if (this.isInsulinProgramSelected()) {
      this.pspInsulinService
      .getInsulinCenters()
      .subscribe((res) => {
        this.referralCenters = res.data;
      });
    }
  }

  onFileUploaded(files: File[]) {
    for (let i = 0; i < files?.length; i++) {
      if (this.isFileValid(files[i])) {
        this.uploadedDocuments.push(files[i]);
      }
    }
  }

  isFileValid(file: File): boolean {
    let existingFileNames = this.uploadedDocuments?.map(e => e.name);
    const filesExtensions = this.uploadFileTypes.split(',').map(e => e.trim());
    const fileName = file.name.split('.');
    const fileExtension = fileName[fileName.length - 1];

    let maxDocumentsExceeded = this.uploadedDocuments.length >= this.maxDocumentsToUpload;
    if(maxDocumentsExceeded) {
      showNotification(
        'danger',
        `You can upload a maximum of ${this.maxDocumentsToUpload} files. Only the first ${this.maxDocumentsToUpload} files were added.`,
        this.toastr
      );
      return false;
    }

    let isFileNameExist = existingFileNames.find(e => e == file.name);
    if(isFileNameExist) {
      showNotification(
        'danger',
        `File ${file.name} is already added.`,
        this.toastr
      );
      return false;
    }

    let isValidExtension = filesExtensions.includes(`.${fileExtension.toLowerCase()}`);
    if(!isValidExtension) {
      showNotification(
        'danger',
        `File format is not supported. Only JPG, JPEG, PNG, GIF and PDF formats are allowed.`,
        this.toastr
      );
      return false;
    }

    return !isFileNameExist
      && !maxDocumentsExceeded
      && isValidExtension;
  }
  
  isInsulinProgramSelected(): boolean {
    const programName = this.programList?.find(e => e.id === this.newPatient.programId)?.name?.toLowerCase();
  
    return this.companyId === PspCompanyEnum.Eva && (programName === ProgramNameEnum.D_Pen || programName === ProgramNameEnum.Neo_Pen);
  }

  changeResponseToFormData(): FormData {
    const formData = new FormData();
    formData.append('programId', this.newPatient.programId?.toString() || '');
    formData.append('pspTypeId', this.newPatient.pspTypeId?.toString() || '');
    formData.append('patientMobileNumber', this.newPatient.patientMobileNumber || '');
    formData.append('patientFullName', this.newPatient.patientFullName || '');
    formData.append('patientCityId', this.newPatient.patientCityId?.toString() || '');
    formData.append('patientAreaId', this.newPatient.patientAreaId || '');
    formData.append('patientDOB', this.newPatient.patientDOB?.toDateString() || '');
    formData.append('patientNationalId', this.newPatient.patientNationalId || '');
    formData.append('patientPassportId', this.newPatient.patientPassportId || '');
    formData.append('subscriptionHistory', this.newPatient.subscriptionHistory || '');
    formData.append('doctorName', this.newPatient.doctorName || '');
    formData.append('hospitalName', this.newPatient.hospitalName || '');
    formData.append('careReceiverNationalId', this.newPatient.careReceiverNationalId || '');
    formData.append('careReceiverPassportId', this.newPatient.careReceiverPassportId || '');
    formData.append('careReceiverFullName', this.newPatient.careReceiverFullName || '');
    formData.append('careReceiverDOB', this.newPatient.careReceiverDOB?.toDateString() || '');
    formData.append('relationId', this.newPatient.relationId?.toString() || '');
    formData.append('doctorCode', this.newPatient.doctorCode || '');
    formData.append('insulinTypeId', this.newPatient.insulinTypeId?.toString() || '');
    formData.append('InsulinCenterId', this.newPatient.referralCenterId?.toString() || '');

    for (let i = 0; i < this.uploadedDocuments?.length; i++) {
      formData.append('Files', this.uploadedDocuments[i]);
    }
    return formData;
  }

  clearDocuments(): void {
    this.uploadedDocuments = [];
  }

}
